import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  MapPin,
  Plus,
  Mail,
  Phone,
  Users as UsersIcon,
  Loader2,
  MoreHorizontal,
  FileText,
  ClipboardCheck,
  CalendarDays,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  ClipboardList,
  Building2,
  Upload,
  Paperclip,
  Download,
  Eye,
  X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  EmployeeFormSheet,
  EmployeeStatusBadge,
  fullName,
  initials,
  formatDateHu,
} from "@/components/EmployeeFormSheet";
import { SiteFormSheet } from "@/components/SiteFormSheet";
import { InspectionFormDialog } from "@/components/InspectionFormDialog";
import {
  AUTHORITY_OPTIONS,
  authorityToFormState,
  authorityFromFormState,
} from "@/lib/authorities";

const DOCUMENT_TYPES = [
  { value: "permit", label: "Engedély" },
  { value: "certificate", label: "Tanúsítvány" },
  { value: "contract", label: "Szerződés" },
  { value: "report", label: "Jelentés" },
  { value: "other", label: "Egyéb" },
];

const INSPECTION_RESULTS = [
  { value: "new", label: "Új bejegyzés", icon: ClipboardList, tone: "muted" },
  { value: "investigating", label: "Kivizsgálás alatt", icon: Clock, tone: "amber" },
  { value: "closed", label: "Lezárva", icon: CheckCircle2, tone: "emerald" },
];

// Backwards-compat: ha még csak régi "result" érték van.
const LEGACY_RESULT_TO_STATUS = {
  pending: "new",
  passed: "closed",
  warning: "investigating",
  failed: "closed",
};
const inspectionStatusOf = (i) =>
  i?.status || LEGACY_RESULT_TO_STATUS[i?.result] || "new";

const RESULT_TONES = {
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400",
  amber:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400",
  red:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400",
  muted: "border-border bg-muted text-muted-foreground",
};

function documentTypeLabel(t) {
  return DOCUMENT_TYPES.find((d) => d.value === t)?.label || "Egyéb";
}

function isExpired(value) {
  if (!value) return false;
  const d = new Date(value);
  return !Number.isNaN(d.getTime()) && d < new Date();
}

function isExpiringSoon(value, days = 30) {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const diff = (d - now) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= days;
}

export default function SiteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [site, setSite] = useState(null);
  const [company, setCompany] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("data");

  const [editSiteOpen, setEditSiteOpen] = useState(false);
  const [empOpen, setEmpOpen] = useState(false);

  const [docOpen, setDocOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);

  const [inspOpen, setInspOpen] = useState(false);
  const [editingInsp, setEditingInsp] = useState(null);

  const loadData = async (showLoader = false) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const [siteRes, docsRes, inspRes, empRes] = await Promise.all([
        api.get(`/sites/${id}`),
        api.get(`/sites/${id}/documents`),
        api.get(`/sites/${id}/inspections`),
        api.get(`/sites/${id}/employees`),
      ]);
      setSite(siteRes.data);
      setDocuments(docsRes.data);
      setInspections(inspRes.data);
      setEmployees(empRes.data);
      if (siteRes.data.company_id) {
        try {
          const c = await api.get(`/companies/${siteRes.data.company_id}`);
          setCompany(c.data);
        } catch (e) {
          setCompany(null);
        }
      }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Nem sikerült betölteni.");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refresh = () => loadData(false);

  const upsertEmployee = (saved) => {
    if (!saved || (saved.site_id && saved.site_id !== id)) return;
    setEmployees((prev) => {
      const idx = prev.findIndex((e) => e._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev];
    });
  };

  const upsertDocument = (saved) => {
    if (!saved) return;
    setDocuments((prev) => {
      const idx = prev.findIndex((d) => d._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev];
    });
  };

  const upsertInspection = (saved) => {
    if (!saved) return;
    setInspections((prev) => {
      const idx = prev.findIndex((i) => i._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev];
    });
  };

  const docStats = useMemo(() => {
    const total = documents.length;
    const expired = documents.filter((d) => isExpired(d.expires_at)).length;
    const expiring = documents.filter(
      (d) => !isExpired(d.expires_at) && isExpiringSoon(d.expires_at)
    ).length;
    return { total, expired, expiring };
  }, [documents]);

  const inspStats = useMemo(() => {
    const total = inspections.length;
    const newCount = inspections.filter((i) => inspectionStatusOf(i) === "new").length;
    const investigating = inspections.filter((i) => inspectionStatusOf(i) === "investigating").length;
    const closed = inspections.filter((i) => inspectionStatusOf(i) === "closed").length;
    return { total, newCount, investigating, closed };
  }, [inspections]);

  const onDeleteSite = async () => {
    if (!window.confirm(`Biztosan törlöd "${site.name}" telephelyet?`)) return;
    try {
      await api.delete(`/sites/${id}`);
      toast.success("Telephely törölve.");
      navigate(company ? `/companies/${company._id}` : "/companies");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  const onDeleteDocument = async (doc) => {
    if (!window.confirm(`Biztosan törlöd "${doc.title}" dokumentumot?`)) return;
    try {
      await api.delete(`/documents/${doc._id}`);
      toast.success("Dokumentum törölve.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  const onDeleteInspection = async (insp) => {
    if (!window.confirm(`Biztosan törlöd ezt az ellenőrzést?`)) return;
    try {
      await api.delete(`/inspections/${insp._id}`);
      toast.success("Ellenőrzés törölve.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  if (loading) {
    return (
      <div
        data-testid="site-detail-loading"
        className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground"
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="flex flex-col items-center gap-3 py-20 text-center">
        <p className="text-sm text-muted-foreground">{error || "Nem található."}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/companies">
            <ArrowLeft className="h-3.5 w-3.5" /> Vissza
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="site-detail-page" className="space-y-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 text-muted-foreground"
        >
          <Link
            to={company ? `/companies/${company._id}` : "/companies"}
            data-testid="back-to-company"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {company ? company.name : "Cégek"}
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-5">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-foreground text-background">
            <MapPin className="h-7 w-7" strokeWidth={1.5} />
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Telephely adatlap
            </p>
            <h1
              data-testid="site-name"
              className="text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {site.name}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <SiteStatusBadge value={site.status} />
              {company && (
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                  <Link to={`/companies/${company._id}`} className="hover:text-foreground">
                    {company.name}
                  </Link>
                </span>
              )}
              {site.city && (
                <span>
                  · {site.postal_code ? `${site.postal_code} ` : ""}
                  {site.city}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setEditSiteOpen(true)}
            data-testid="site-edit-button"
          >
            <Pencil className="h-4 w-4" /> Szerkesztés
          </Button>
          <Button
            variant="outline"
            onClick={onDeleteSite}
            className="text-destructive hover:text-destructive"
            data-testid="site-delete-button"
          >
            <Trash2 className="h-4 w-4" /> Törlés
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList data-testid="site-detail-tabs">
          <TabsTrigger value="data" data-testid="tab-data">
            Adatok
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            Dokumentumok ({documents.length})
          </TabsTrigger>
          <TabsTrigger value="inspections" data-testid="tab-inspections">
            Hatósági ellenőrzések ({inspections.length})
          </TabsTrigger>
        </TabsList>

        {/* TAB: Adatok */}
        <TabsContent value="data" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <DetailCard
              title="Telephely adatai"
              testId="card-site-data"
              className="lg:col-span-2"
            >
              <DataGrid>
                <DataRow label="Megnevezés" value={site.name} />
                <DataRow
                  label="Vezető"
                  value={site.manager_name}
                  icon={<UsersIcon className="h-3.5 w-3.5" strokeWidth={1.75} />}
                />
                <DataRow
                  label="E-mail"
                  value={site.email}
                  mono
                  icon={<Mail className="h-3.5 w-3.5" strokeWidth={1.75} />}
                />
                <DataRow
                  label="Telefon"
                  value={site.phone}
                  icon={<Phone className="h-3.5 w-3.5" strokeWidth={1.75} />}
                />
                <DataRow label="Irányítószám" value={site.postal_code} mono />
                <DataRow label="Város" value={site.city} />
                <DataRow label="Cím" value={site.address} multiline className="sm:col-span-2" />
                {site.notes && (
                  <DataRow label="Megjegyzés" value={site.notes} multiline className="sm:col-span-2" />
                )}
              </DataGrid>
            </DetailCard>

            <DetailCard title="Összegzés" testId="card-site-summary">
              <DataGrid cols={1}>
                <DataRow
                  label="Munkavállaló"
                  value={employees.length}
                  mono
                  icon={<UsersIcon className="h-3.5 w-3.5" strokeWidth={1.75} />}
                />
                <DataRow
                  label="Dokumentum"
                  value={documents.length}
                  mono
                  icon={<FileText className="h-3.5 w-3.5" strokeWidth={1.75} />}
                />
                <DataRow
                  label="Ellenőrzés"
                  value={inspections.length}
                  mono
                  icon={<ClipboardCheck className="h-3.5 w-3.5" strokeWidth={1.75} />}
                />
              </DataGrid>
            </DetailCard>
          </div>

          {/* Employees list */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium tracking-tight">
                Munkavállalók a telephelyen ({employees.length})
              </h3>
              <Button
                size="sm"
                data-testid="add-employee-to-site-button"
                onClick={() => setEmpOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Munkavállaló hozzáadása
              </Button>
            </div>
            <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
              {employees.length === 0 ? (
                <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
                  <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
                    <UsersIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                  </div>
                  <p className="text-sm font-medium">Nincsenek munkavállalók</p>
                  <p className="text-xs text-muted-foreground">
                    A telephelyhez még nem rendeltél munkavállalót.
                  </p>
                </div>
              ) : (
                <table className="w-full min-w-[640px] text-sm" data-testid="site-employees-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                      <Th>Név</Th>
                      <Th>Beosztás</Th>
                      <Th>Elérhetőség</Th>
                      <Th>Állapot</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp) => (
                      <tr
                        key={emp._id}
                        data-testid={`site-employee-row-${emp._id}`}
                        onClick={() => navigate(`/employees/${emp._id}`)}
                        className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                      >
                        <Td>
                          <div className="flex items-center gap-3">
                            <span className="grid h-7 w-7 place-items-center rounded-full bg-foreground/90 text-background text-[10px] font-semibold">
                              {initials(emp)}
                            </span>
                            <span className="font-medium">
                              {emp.titulus ? `${emp.titulus} ` : ""}
                              {fullName(emp)}
                            </span>
                          </div>
                        </Td>
                        <Td className="text-muted-foreground">{emp.position || "—"}</Td>
                        <Td>
                          <div className="flex flex-col gap-0.5 text-[12px]">
                            {emp.email && (
                              <span className="font-mono text-muted-foreground">{emp.email}</span>
                            )}
                            {emp.phone && (
                              <span className="text-muted-foreground">{emp.phone}</span>
                            )}
                            {!emp.email && !emp.phone && (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        </Td>
                        <Td>
                          <EmployeeStatusBadge value={emp.status} />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </TabsContent>

        {/* TAB: Dokumentumok */}
        <TabsContent value="documents" className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Összes" value={docStats.total} testId="stat-doc-total" />
            <StatCard
              label="Hamarosan lejár"
              value={docStats.expiring}
              testId="stat-doc-expiring"
              tone={docStats.expiring > 0 ? "amber" : undefined}
            />
            <StatCard
              label="Lejárt"
              value={docStats.expired}
              testId="stat-doc-expired"
              tone={docStats.expired > 0 ? "red" : undefined}
            />
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              A telephelyhez tartozó dokumentumok (engedélyek, szerződések, tanúsítványok).
            </p>
            <Button
              size="sm"
              data-testid="new-document-button"
              onClick={() => {
                setEditingDoc(null);
                setDocOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Új dokumentum
            </Button>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
            {documents.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />}
                title="Nincsenek dokumentumok"
                description="Adj hozzá engedélyeket, szerződéseket vagy egyéb dokumentumokat."
                onAction={() => {
                  setEditingDoc(null);
                  setDocOpen(true);
                }}
                actionLabel="Új dokumentum"
                testId="documents-empty-state"
              />
            ) : (
              <table className="w-full min-w-[640px] text-sm" data-testid="documents-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                    <Th>Cím</Th>
                    <Th>Típus</Th>
                    <Th>Fájl</Th>
                    <Th>Kiállítva</Th>
                    <Th>Lejár</Th>
                    <Th className="pr-4 text-right">Műveletek</Th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((d) => {
                    const expired = isExpired(d.expires_at);
                    const expiring = !expired && isExpiringSoon(d.expires_at);
                    return (
                      <tr
                        key={d._id}
                        data-testid={`document-row-${d._id}`}
                        onClick={() => setViewerDoc(d)}
                        className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                      >
                        <Td>
                          <div className="flex items-start gap-3">
                            <FileText
                              className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                              strokeWidth={1.75}
                            />
                            <div className="flex flex-col leading-tight">
                              <span className="font-medium">{d.title}</span>
                              {d.notes && (
                                <span className="text-[11px] text-muted-foreground">
                                  {d.notes}
                                </span>
                              )}
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <span className="inline-flex h-5 items-center rounded-md border border-border bg-muted px-2 text-[11px] font-medium text-muted-foreground">
                            {documentTypeLabel(d.type)}
                          </span>
                        </Td>
                        <Td>
                          {d.file_url ? (
                            <span
                              data-testid={`document-file-attached-${d._id}`}
                              className="inline-flex items-center gap-1.5 text-[12.5px] text-foreground"
                            >
                              <Paperclip
                                className="h-3.5 w-3.5"
                                strokeWidth={1.75}
                              />
                              <span className="max-w-[160px] truncate font-mono text-[11.5px]">
                                {d.file_name || "fájl"}
                              </span>
                            </span>
                          ) : (
                            <span
                              data-testid={`document-file-none-${d._id}`}
                              className="text-[12.5px] text-muted-foreground"
                            >
                              —
                            </span>
                          )}
                        </Td>
                        <Td className="text-muted-foreground">{formatDateHu(d.issued_date)}</Td>
                        <Td>
                          {d.expires_at ? (
                            <span
                              className={cn(
                                "inline-flex items-center gap-1.5 text-[12.5px]",
                                expired && "text-red-600 dark:text-red-400 font-medium",
                                expiring && "text-amber-600 dark:text-amber-400 font-medium",
                                !expired && !expiring && "text-muted-foreground"
                              )}
                            >
                              <CalendarDays className="h-3 w-3" strokeWidth={1.75} />
                              {formatDateHu(d.expires_at)}
                              {expired && " · lejárt"}
                              {expiring && " · hamarosan"}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </Td>
                        <Td className="pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                data-testid={`document-actions-${d._id}`}
                                aria-label="Dokumentum műveletek"
                                className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`view-document-${d._id}`}
                                onClick={() => setViewerDoc(d)}
                              >
                                <Eye className="h-4 w-4" />
                                Megnyitás
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`edit-document-${d._id}`}
                                onClick={() => {
                                  setEditingDoc(d);
                                  setDocOpen(true);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                                Szerkesztés
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`delete-document-${d._id}`}
                                onClick={() => onDeleteDocument(d)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Törlés
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* TAB: Hatósági ellenőrzések */}
        <TabsContent value="inspections" className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Összes" value={inspStats.total} testId="stat-insp-total" />
            <StatCard
              label="Új bejegyzés"
              value={inspStats.newCount}
              testId="stat-insp-new"
              tone={inspStats.newCount > 0 ? "amber" : undefined}
            />
            <StatCard
              label="Kivizsgálás alatt"
              value={inspStats.investigating}
              testId="stat-insp-investigating"
              tone={inspStats.investigating > 0 ? "amber" : undefined}
            />
            <StatCard
              label="Lezárva"
              value={inspStats.closed}
              testId="stat-insp-closed"
              tone="emerald"
            />
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Hatósági ellenőrzések nyilvántartása (NAV, ÁNTSZ, NÉBIH, tűzoltóság stb.).
            </p>
            <Button
              size="sm"
              data-testid="new-inspection-button"
              onClick={() => {
                setEditingInsp(null);
                setInspOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Új ellenőrzés
            </Button>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
            {inspections.length === 0 ? (
              <EmptyState
                icon={
                  <ClipboardCheck
                    className="h-4 w-4 text-muted-foreground"
                    strokeWidth={1.75}
                  />
                }
                title="Nincsenek ellenőrzések"
                description="Rögzítsd a telephelyen történt hatósági ellenőrzéseket."
                onAction={() => {
                  setEditingInsp(null);
                  setInspOpen(true);
                }}
                actionLabel="Új ellenőrzés"
                testId="inspections-empty-state"
              />
            ) : (
              <table className="w-full min-w-[640px] text-sm" data-testid="inspections-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                    <Th>Hatóság</Th>
                    <Th>Tárgya</Th>
                    <Th>Ellenőr</Th>
                    <Th>Dátum</Th>
                    <Th>Státusz</Th>
                    <Th>Büntetés</Th>
                    <Th className="pr-4 text-right">Műveletek</Th>
                  </tr>
                </thead>
                <tbody>
                  {inspections.map((insp) => (
                    <tr
                      key={insp._id}
                      data-testid={`inspection-row-${insp._id}`}
                      onClick={() => navigate(`/inspections/${insp._id}`)}
                      className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                    >
                      <Td>
                        <span className="font-medium">{insp.authority}</span>
                      </Td>
                      <Td className="text-muted-foreground">{insp.subject || "—"}</Td>
                      <Td className="text-muted-foreground">{insp.inspector_name || "—"}</Td>
                      <Td className="text-muted-foreground">
                        {formatDateHu(insp.inspection_date)}
                      </Td>
                      <Td>
                        <InspectionResultBadge value={inspectionStatusOf(insp)} />
                      </Td>
                      <Td>
                        {insp.penalty === "had_penalty" ? (
                          <span
                            data-testid={`inspection-penalty-${insp.penalty}`}
                            className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700"
                          >
                            {insp.penalty_amount != null
                              ? `${Number(insp.penalty_amount).toLocaleString("hu-HU")} Ft`
                              : "Volt"}
                          </span>
                        ) : insp.penalty === "no_penalty" ? (
                          <span
                            data-testid={`inspection-penalty-${insp.penalty}`}
                            className="text-[11px] text-emerald-700"
                          >
                            Nem volt
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </Td>
                      <Td className="pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`inspection-actions-${insp._id}`}
                              aria-label="Ellenőrzés műveletek"
                              className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              data-testid={`open-inspection-${insp._id}`}
                              onClick={() => navigate(`/inspections/${insp._id}`)}
                            >
                              <Eye className="h-4 w-4" />
                              Adatlap megnyitása
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`edit-inspection-${insp._id}`}
                              onClick={() => {
                                setEditingInsp(insp);
                                setInspOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                              Szerkesztés
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`delete-inspection-${insp._id}`}
                              onClick={() => onDeleteInspection(insp)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                              Törlés
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs / Sheets */}
      <SiteFormSheet
        open={editSiteOpen}
        onOpenChange={setEditSiteOpen}
        companyId={site.company_id}
        site={site}
        onSaved={(saved) => {
          if (saved) setSite((s) => ({ ...s, ...saved }));
          refresh();
        }}
      />
      <EmployeeFormSheet
        open={empOpen}
        onOpenChange={setEmpOpen}
        employee={null}
        defaultCompanyId={site.company_id || ""}
        defaultSiteId={id}
        onSaved={(saved) => {
          upsertEmployee(saved);
          refresh();
        }}
      />
      <DocumentDialog
        open={docOpen}
        onOpenChange={setDocOpen}
        siteId={id}
        document={editingDoc}
        onSaved={(saved) => {
          upsertDocument(saved);
          refresh();
        }}
      />
      <DocumentViewerDialog
        document={viewerDoc}
        onClose={() => setViewerDoc(null)}
        onEdit={(d) => {
          setViewerDoc(null);
          setEditingDoc(d);
          setDocOpen(true);
        }}
      />
      <InspectionFormDialog
        open={inspOpen}
        onOpenChange={setInspOpen}
        siteId={id}
        inspection={editingInsp}
        onSaved={(saved) => {
          upsertInspection(saved);
          refresh();
        }}
      />
    </div>
  );
}

/* ------------ Sub components ------------ */

function SiteStatusBadge({ value }) {
  const isActive = value === "active";
  return (
    <span
      data-testid={`site-status-${value}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium",
        isActive
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400"
          : "border-border bg-muted text-muted-foreground"
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          isActive ? "bg-emerald-500" : "bg-muted-foreground/60"
        )}
      />
      {isActive ? "Aktív" : "Inaktív"}
    </span>
  );
}

function InspectionResultBadge({ value }) {
  const meta = INSPECTION_RESULTS.find((r) => r.value === value) || INSPECTION_RESULTS[0];
  const Icon = meta.icon;
  return (
    <span
      data-testid={`inspection-status-${value}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium",
        RESULT_TONES[meta.tone]
      )}
    >
      <Icon className="h-3 w-3" strokeWidth={1.75} />
      {meta.label}
    </span>
  );
}

const emptyDoc = {
  title: "",
  type: "permit",
  issued_date: "",
  expires_at: "",
  notes: "",
};

const ACCEPTED_DOC_MIME = "application/pdf,image/jpeg,image/png,image/webp";
const MAX_DOC_BYTES = 20 * 1024 * 1024;

function formatBytes(b) {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} kB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentDialog({ open, onOpenChange, siteId, document, onSaved }) {
  const [form, setForm] = useState(emptyDoc);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [removeExistingFile, setRemoveExistingFile] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (document) {
      setForm({
        title: document.title || "",
        type: document.type || "other",
        issued_date: document.issued_date || "",
        expires_at: document.expires_at || "",
        notes: document.notes || "",
      });
    } else {
      setForm(emptyDoc);
    }
    setSelectedFile(null);
    setRemoveExistingFile(false);
    setErr("");
  }, [open, document]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_DOC_BYTES) {
      setErr("A fájl túl nagy (max 20 MB).");
      e.target.value = "";
      return;
    }
    setErr("");
    setSelectedFile(f);
    setRemoveExistingFile(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      payload.title = form.title;
      payload.type = form.type;

      let docId;
      let savedDoc;
      if (document) {
        const { data } = await api.patch(`/documents/${document._id}`, payload);
        docId = data._id;
        savedDoc = data;
      } else {
        const { data } = await api.post(`/sites/${siteId}/documents`, payload);
        docId = data._id;
        savedDoc = data;
      }

      if (removeExistingFile && document?.file_url) {
        const { data } = await api.delete(`/documents/${docId}/file`);
        savedDoc = data;
      }

      if (selectedFile) {
        const fd = new FormData();
        fd.append("file", selectedFile);
        const { data } = await api.post(`/documents/${docId}/file`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        savedDoc = data;
      }

      toast.success(document ? "Dokumentum frissítve." : "Dokumentum létrehozva.");
      onOpenChange(false);
      if (onSaved) onSaved(savedDoc);
    } catch (error) {
      setErr(formatApiError(error?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const existingHasFile = !!document?.file_url && !removeExistingFile;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="document-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {document ? "Dokumentum szerkesztése" : "Új dokumentum"}
          </DialogTitle>
          <DialogDescription>
            Rögzítsd a dokumentum adatait, és — ha van — csatold a fájlt (PDF, JPG, PNG, WebP, max 20 MB).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField label="Cím *" testId="doc-form-title">
            <Input
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Pl. Működési engedély"
            />
          </FormField>
          <FormField label="Típus" testId="doc-form-type">
            <Select
              value={form.type}
              onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Kiállítva" testId="doc-form-issued">
              <DateInput
                value={form.issued_date}
                onChange={(v) =>
                  setForm((f) => ({ ...f, issued_date: v }))
                }
              />
            </FormField>
            <FormField label="Lejárati dátum" testId="doc-form-expires">
              <DateInput
                value={form.expires_at}
                onChange={(v) =>
                  setForm((f) => ({ ...f, expires_at: v }))
                }
              />
            </FormField>
          </div>
          <FormField label="Megjegyzés" testId="doc-form-notes">
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
            />
          </FormField>

          {/* File upload */}
          <FormField label="Csatolt fájl" testId="doc-form-file">
            <div className="space-y-2">
              {existingHasFile && !selectedFile && (
                <div
                  data-testid="doc-form-existing-file"
                  className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm"
                >
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <Paperclip
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                      strokeWidth={1.75}
                    />
                    <span className="truncate font-mono text-[12px]">
                      {document.file_name || "csatolt fájl"}
                    </span>
                    {document.file_size && (
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        · {formatBytes(document.file_size)}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    data-testid="doc-form-remove-existing-file"
                    onClick={() => setRemoveExistingFile(true)}
                    className="text-[11px] text-muted-foreground hover:text-destructive"
                  >
                    Eltávolítás
                  </button>
                </div>
              )}
              {selectedFile && (
                <div
                  data-testid="doc-form-pending-file"
                  className="flex items-center justify-between gap-2 rounded-md border border-foreground/20 bg-foreground/[0.03] px-3 py-2 text-sm"
                >
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <Upload
                      className="h-3.5 w-3.5 shrink-0"
                      strokeWidth={1.75}
                    />
                    <span className="truncate font-mono text-[12px]">
                      {selectedFile.name}
                    </span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      · {formatBytes(selectedFile.size)}
                    </span>
                  </span>
                  <button
                    type="button"
                    data-testid="doc-form-clear-pending-file"
                    onClick={() => setSelectedFile(null)}
                    className="text-[11px] text-muted-foreground hover:text-destructive"
                  >
                    Mégse
                  </button>
                </div>
              )}
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-foreground/[0.03] hover:text-foreground">
                <Upload className="h-4 w-4" strokeWidth={1.75} />
                <span>
                  {existingHasFile || selectedFile
                    ? "Másik fájl választása"
                    : "Fájl kiválasztása (PDF, JPG, PNG)"}
                </span>
                <input
                  type="file"
                  accept={ACCEPTED_DOC_MIME}
                  className="hidden"
                  data-testid="doc-form-file-input"
                  onChange={onPickFile}
                />
              </label>
            </div>
          </FormField>

          {err && (
            <div
              data-testid="document-form-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-document"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="submit-document">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : document ? (
                "Mentés"
              ) : (
                "Létrehozás"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* --------- Document Viewer (overlay) --------- */
function DocumentViewerDialog({ document, onClose, onEdit }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [contentType, setContentType] = useState("");
  const [loadingFile, setLoadingFile] = useState(false);
  const [fileError, setFileError] = useState("");

  const open = !!document;

  useEffect(() => {
    if (!open) {
      setBlobUrl("");
      setContentType("");
      setFileError("");
      return;
    }
    if (!document.file_url) {
      setBlobUrl("");
      setContentType("");
      setFileError("");
      return;
    }
    let url;
    setLoadingFile(true);
    setFileError("");
    api
      .get(`/documents/${document._id}/file`, { responseType: "blob" })
      .then((res) => {
        const ctype =
          document.content_type || res.headers["content-type"] || "application/octet-stream";
        const blob = new Blob([res.data], { type: ctype });
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setContentType(ctype);
      })
      .catch((e) => {
        setFileError(
          formatApiError(e?.response?.data?.detail) ||
            "Nem sikerült betölteni a fájlt."
        );
      })
      .finally(() => setLoadingFile(false));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, document]);

  const onDownload = () => {
    if (!blobUrl || !document) return;
    const a = window.document.createElement("a");
    a.href = blobUrl;
    a.download = document.file_name || `${document.title || "dokumentum"}`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  if (!document) return null;

  const isPdf = contentType.includes("pdf");
  const isImage = contentType.startsWith("image/");

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="document-viewer"
        className="flex max-h-[92vh] w-[92vw] max-w-[92vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl"
      >
        <DialogHeader className="border-b border-border px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-base tracking-tight">
                {document.title}
              </DialogTitle>
              <DialogDescription className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex h-5 items-center rounded-md border border-border bg-muted px-2 text-[11px] font-medium text-muted-foreground">
                  {documentTypeLabel(document.type)}
                </span>
                {document.issued_date && (
                  <span className="text-muted-foreground">
                    Kiállítva: {formatDateHu(document.issued_date)}
                  </span>
                )}
                {document.expires_at && (
                  <span className="text-muted-foreground">
                    · Lejár: {formatDateHu(document.expires_at)}
                  </span>
                )}
                {document.file_name && (
                  <span className="inline-flex items-center gap-1 font-mono text-muted-foreground">
                    · <Paperclip className="h-3 w-3" strokeWidth={1.75} />
                    {document.file_name}
                  </span>
                )}
              </DialogDescription>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {document.file_url && blobUrl && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDownload}
                  data-testid="viewer-download-button"
                >
                  <Download className="h-3.5 w-3.5" />
                  Letöltés
                </Button>
              )}
              {onEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onEdit(document)}
                  data-testid="viewer-edit-button"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Szerkesztés
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div
          data-testid="viewer-content"
          className="flex flex-1 items-stretch justify-center overflow-auto bg-muted/40 p-4"
        >
          {!document.file_url ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <div className="grid h-12 w-12 place-items-center rounded-full border border-border bg-background">
                <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-medium">Nincs csatolt fájl</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Ehhez a dokumentumhoz még nem tartozik fájl. Szerkesztéssel csatolhatsz egyet.
                </p>
              </div>
              {document.notes && (
                <p className="mt-4 max-w-md whitespace-pre-line text-sm text-muted-foreground">
                  {document.notes}
                </p>
              )}
            </div>
          ) : loadingFile ? (
            <div className="flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Fájl betöltése…
            </div>
          ) : fileError ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-destructive">
              <AlertTriangle className="h-5 w-5" strokeWidth={1.5} />
              {fileError}
            </div>
          ) : isPdf ? (
            <iframe
              data-testid="viewer-pdf-frame"
              title={document.title}
              src={blobUrl}
              className="min-h-[70vh] w-full rounded-md border border-border bg-background"
            />
          ) : isImage ? (
            <img
              data-testid="viewer-image"
              src={blobUrl}
              alt={document.title}
              className="max-h-[80vh] max-w-full rounded-md object-contain"
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <FileText className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
              <p className="text-sm">
                Az előnézet ehhez a fájltípushoz nem elérhető.
              </p>
              <Button size="sm" variant="outline" onClick={onDownload}>
                <Download className="h-3.5 w-3.5" /> Letöltés
              </Button>
            </div>
          )}
        </div>

        {document.notes && document.file_url && (
          <div className="border-t border-border bg-[hsl(var(--sipx-elevated))] px-6 py-3 text-xs text-muted-foreground">
            {document.notes}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function DetailCard({ title, children, testId, className }) {
  return (
    <section
      data-testid={testId}
      className={cn(
        "rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]",
        className
      )}
    >
      <header className="border-b border-border px-5 py-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {title}
        </h3>
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function DataGrid({ cols = 2, children }) {
  return (
    <dl
      className={cn(
        "grid gap-x-6 gap-y-3",
        cols === 1 && "grid-cols-1",
        cols === 2 && "grid-cols-1 sm:grid-cols-2"
      )}
    >
      {children}
    </dl>
  );
}

function DataRow({ label, value, icon, mono, multiline, className }) {
  const isEmpty = value === undefined || value === null || value === "";
  return (
    <div className={cn("flex flex-col gap-0.5", className)}>
      <dt className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "inline-flex items-center gap-1.5 text-sm",
          isEmpty && "text-muted-foreground/70",
          mono && "font-mono text-[12.5px]",
          multiline && "whitespace-pre-line"
        )}
      >
        {icon && !isEmpty && <span className="text-muted-foreground">{icon}</span>}
        {isEmpty ? "—" : value}
      </dd>
    </div>
  );
}

function StatCard({ label, value, testId, tone }) {
  const dotClass = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
  }[tone] || "bg-foreground/30";
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] p-4"
    >
      <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        <span className={cn("h-1.5 w-1.5 rounded-full", dotClass)} />
        {label}
      </p>
      <p className="mt-1.5 font-mono text-xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function FormField({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={cn(
        "h-10 whitespace-nowrap px-4 text-left align-middle text-[11px] font-medium uppercase tracking-[0.12em]",
        className
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "", ...rest }) {
  return (
    <td
      className={cn("h-14 whitespace-nowrap px-4 align-middle", className)}
      {...rest}
    >
      {children}
    </td>
  );
}

function EmptyState({ icon, title, description, onAction, actionLabel, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center"
    >
      <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
        {icon}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {onAction && actionLabel && (
        <Button onClick={onAction} variant="outline" className="mt-2 h-8">
          <Plus className="h-3.5 w-3.5" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
